import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, CloudUpload, Plus, Edit } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import Pagination from "../components/Paginate";
import DateRangePicker from '../components/DateRangePicker';
import axiosInstance from '../utils/axiosInstance';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import LoadingSpinner from '../components/LoadingSpinner';

const ApiVersions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [apiVersions, setApiVersions] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });
  const [loading, setLoading] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [uploading, setUploading] = useState(false);
  const defaultFilters = {
    search: "",
    page: 1,
    limit: 10,
    startDate: null,
    endDate: null
  };

  const [filters, setFilters] = useState(defaultFilters);

  const buildQueryString = (filters) => {
    const queryParams = new URLSearchParams();
    
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.startDate) queryParams.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) queryParams.append('endDate', filters.endDate.toISOString());
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);
    
    return queryParams.toString();
  };

  const getSerialNumber = (index) => {
    return ((pagination.currentPage - 1) * pagination.itemsPerPage) + index + 1;
  };

  useEffect(() => {
    const restoreFilters = () => {
      let initialFilters = { ...defaultFilters };
  
      // Get values from URL if they exist
      const page = searchParams.get('page');
      const limit = searchParams.get('limit');
      const search = searchParams.get('search');
      const startDate = searchParams.get('startDate');
      const endDate = searchParams.get('endDate');
  
      // Only override defaults if URL params exist and are valid
      if (page) initialFilters.page = Math.max(1, parseInt(page) || 1);
      if (limit) initialFilters.limit = Math.max(1, parseInt(limit) || 10);
      if (search) initialFilters.search = search;
      if (startDate) initialFilters.startDate = new Date(startDate);
      if (endDate) initialFilters.endDate = new Date(endDate);
  
      setFilters(initialFilters);
      setLocalSearch(initialFilters.search || '');
      setIsInitialized(true);
    };
  
    restoreFilters();
  }, []);

  const fetchApiVersions = async (queryString) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`api-versions/list?${queryString}`);
      setApiVersions(response.data.data.apiVersions);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error("Error fetching API versions:", error);
      showErrorToast(error.response?.data?.message || 'Error fetching API versions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isInitialized) return;

    const updateUrlAndFetch = () => {
      const isIncompleteDateRange = 
        (filters.startDate && !filters.endDate) || 
        (!filters.startDate && filters.endDate);

      if (isIncompleteDateRange) return;

      const finalFilters = {
        ...filters,
        page: filters.page || 1,
        limit: filters.limit || 10
      };

      const queryString = buildQueryString(finalFilters);
      const currentQueryString = searchParams.toString();

      const currentParams = new URLSearchParams(currentQueryString);
      const newParams = new URLSearchParams(queryString);

      const paramsChanged = currentParams.toString() !== newParams.toString();

      if (paramsChanged) {
        setSearchParams(queryString ? `?${queryString}` : {}, { replace: true });
      }

      fetchApiVersions(queryString);
    };

    updateUrlAndFetch();
  }, [filters, isInitialized]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setFilters(prev => ({ ...prev, search: value, page: 1 }));
      }, 500),
    []
  );

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleDateChange = (dates) => {
    if (!dates.startDate && !dates.endDate) {
      setFilters(prev => ({ ...prev, startDate: null, endDate: null, page: 1 }));
    } else {
      setFilters(prev => ({ ...prev, ...dates, page: 1 }));
    }
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setLocalSearch("");
    debouncedSearch.cancel();
    setSearchParams({}, { replace: true });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      await axiosInstance.post('api-versions/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showSuccessToast('File uploaded successfully!');
      const queryString = buildQueryString(filters);
      fetchApiVersions(queryString);
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Error uploading file!');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">API Versions</h1>
        <div className="flex items-center gap-2">
  <Link
    to="/apiVersions/create"
    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center gap-2"
  >
    <Plus className="h-5 w-5" /> {/* Set icon size if needed */}
    <span>Create</span>
  </Link>
</div>

      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search API versions..."
            value={localSearch}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="w-full sm:w-[300px]">
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleDateChange}
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div><LoadingSpinner /></div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">S.No</th>
                <th className="px-6 py-3 text-left">IOS Version</th>
                <th className="px-6 py-3 text-left">Android Version</th>
                <th className="px-6 py-3 text-left">Release Date </th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {apiVersions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                    No API versions found
                  </td>
                </tr>
              ) : (
                apiVersions.map((version, index) => (
                  <tr key={version.id}>
                    <td className="px-6 py-4">{getSerialNumber(index)}</td>
                    <td className="px-6 py-4">{version.iosVersion}</td>
                    <td className="px-6 py-4">{version.androidVersion}</td>
                    <td className="px-6 py-4">{new Date(version.createdAt).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
  <Link
    to={`/apiVersions/edit/${version.id}`}
    className="text-blue-500 hover:text-blue-700 flex items-center gap-2"
  >
    <Edit className="h-5 w-5" /> {/* Set icon size if needed */}
    <span>Edit</span>
  </Link>
</td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="pt-4">
        <Pagination
          currentPage={filters.page}
          totalItems={pagination.totalItems}
          pageSize={filters.limit}
          onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
          onPageSizeChange={(limit) => setFilters(prev => ({ ...prev, limit, page: 1 }))}
        />
      </div>
    </div>
  );
};

export default ApiVersions;
